// METACITY POS 주문 API (okdcApi 미러 — 백엔드 /api/metacity/* 호출)
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost:5000';
};
const API_URL = getApiUrl();

// 마스터 데이터 동기화
export const syncMetacityMasterData = async (onProgress) => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) throw new Error('인증 토큰이 없습니다.');
    const payload = JSON.parse(atob(token.split('.')[1]));
    const storeId = payload.storeId || payload.id;

    if (onProgress) onProgress('METACITY 동기화 요청 중...');
    const response = await fetch(`${API_URL}/api/metacity/sync/master`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ storeId })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || result.error || 'METACITY 동기화 실패');
    if (onProgress) onProgress('동기화 완료!');
    return { success: true, message: result.message || 'METACITY 마스터 데이터 동기화 완료' };
  } catch (error) {
    console.error('METACITY 동기화 오류:', error);
    return { success: false, message: error.message || 'METACITY 동기화 중 오류가 발생했습니다.' };
  }
};

// 카트 → METACITY 주문 포맷 (8020 ITEM_INFO). 옵션(selectedOptions)→SETITEM.
export const formatOrderForMetacity = (cartItems, tableCode, tableName = '') => {
  const items = cartItems.map((item) => {
    const basePrice = parseInt(item.menuItem?.price || item.menuItem?.basePrice || item.price || 0) || 0;
    const qty = item.quantity || 1;
    const amt = basePrice * qty;

    const setItems = [];
    const pushOpt = (o) => {
      if (o && typeof o === 'object' && (o.name || o.option_name_ko)) {
        const oprice = parseInt(o.option_price || o.additional_price || o.price || 0) || 0;
        const oqty = o.quantity || o.qty || 1;
        const oamt = oprice * oqty;
        setItems.push({
          prodICd: o.option_id || o.prod_i_cd || o.prod_cd || '',
          prodINm: o.name || o.option_name_ko || '',
          qty: oqty,
          amt: oamt,
          vat: Math.round(oamt / 11),
        });
      }
    };
    const so = item.selectedOptions;
    if (Array.isArray(so)) so.forEach(pushOpt);
    else if (so && typeof so === 'object') Object.values(so).forEach((v) => (Array.isArray(v) ? v.forEach(pushOpt) : pushOpt(v)));

    return {
      prodCd: item.menuItem?.prod_cd || item.menuItem?.menu_code || item.prod_cd || '',
      prodNm: item.menuItem?.name_ko || item.menuItem?.menu_name || item.menuItem?.name || '',
      qty,
      amt,
      vat: Math.round(amt / 11),
      dc: 0,
      setItems,
    };
  });
  return { tableCode, tableName, items };
};

// 주문 전송.
// reviewBenefit = { name } | null — POS 로는 전송하지 않고 PC 알림창의 리뷰 서비스 슬롯에만 표시한다.
export const sendOrderToMetacity = async (orderData, storeId, reviewBenefit = null) => {
  try {
    const response = await fetch(`${API_URL}/api/metacity/order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        storeId,
        tableCode: orderData.tableCode,
        tableName: orderData.tableName || '',
        items: orderData.items,
        reviewBenefit,
      })
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      throw new Error(result.error || 'METACITY 주문 전송 실패');
    }
    // displayOrderNo = 우리 통일 표시번호(주문완료·영수증 공통). 없으면 orderNo 폴백.
    return { success: true, message: '주문이 전송되었습니다.', orderNumber: result.displayOrderNo || result.orderNo, data: result.data };
  } catch (error) {
    console.error('METACITY 주문 전송 오류:', error);
    return { success: false, message: error.message || 'METACITY 주문 전송 중 오류가 발생했습니다.' };
  }
};

// 현주문내역 조회 (주문내역 페이지용)
export const getMetacityOrderInfo = async (storeId, tableCode) => {
  try {
    const response = await fetch(`${API_URL}/api/metacity/order-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableCode })
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      return { success: false, orders: [], error: result.error };
    }
    return { success: true, orders: result.orders || [] };
  } catch (error) {
    console.error('METACITY 주문내역 조회 오류:', error);
    return { success: false, orders: [], error: error.message };
  }
};

// 주문 직후 POS 실제 주문번호 조회 (주문완료 페이지 표시용) — okdc fetchOKDCTableOrderNumber 미러.
// 8020 응답엔 우리 ORDER_NO만 에코되고 POS 번호가 없어서, 8060 조회로 POS 번호를 가져온다.
export const fetchMetacityTableOrderNumber = async (storeId, tableCode) => {
  try {
    const orderInfo = await getMetacityOrderInfo(storeId, tableCode);
    if (!orderInfo.success || !orderInfo.orders || orderInfo.orders.length === 0) {
      return { success: true, orderNumber: null };
    }
    const latestOrder = orderInfo.orders[0];
    return { success: true, orderNumber: latestOrder.orderNo || null };
  } catch (error) {
    console.error('[METACITY] 테이블 주문번호 조회 오류:', error);
    return { success: true, orderNumber: null };
  }
};

// 테이블에 활성 주문 존재 여부 (신규/추가 구분용)
export const checkTableOrderExists = async (storeId, tableCode) => {
  try {
    const { success, orders } = await getMetacityOrderInfo(storeId, tableCode);
    if (!success) return { hasOrder: false };
    return { hasOrder: (orders || []).some(o => (o.items || []).length > 0) };
  } catch (error) {
    console.error('METACITY 테이블 주문 확인 오류:', error);
    return { hasOrder: false };
  }
};

// 직원 호출 (우리 클라 팝업)
export const sendCallToMetacity = async (storeId, tableName, reasonIds) => {
  try {
    const response = await fetch(`${API_URL}/api/metacity/staff-call`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableName, reasonIds })
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      throw new Error(result.message || '직원 호출 실패');
    }
    return { success: true, message: result.message };
  } catch (error) {
    console.error('METACITY 직원 호출 오류:', error);
    return { success: false, message: error.message };
  }
};
